import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getApi, downloadApi } from '../../../Services/apiService';
import { CheckrReportView, getBlobErrorMessage } from '../../../LandlordComponent/PropertiesComponent/TenantDetail/BackgroundCheckReport';

export const MY_REPORT_URL = '/verification/tenant/checkr/my-report';
const REPORT_PDF_URL = '/verification/tenant/checkr/report-pdf';

// The tenant's own Checkr report: for ?property_id=..., or their latest check
const MyBackgroundCheckReport = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get('property_id') || '';
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const response = await getApi(MY_REPORT_URL, propertyId ? { params: { property_id: propertyId } } : {});
        if (cancelled) return;
        if (response?.statusCode === 200) {
          setReport(response?.data ?? null);
        } else {
          toast.error(response?.message || 'Failed to fetch background check report.');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching Checkr report:', error);
        toast.error(error?.response?.data?.message || 'Failed to fetch background check report. Please try again.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(propertyId ? `/tenant/properties/${propertyId}` : '/tenant/settings/background-check');
  };

  const handleDownload = async () => {
    // The endpoint needs a property; the latest-check view has none in the URL
    const reportPropertyId = propertyId || report?.property_id;
    if (!reportPropertyId) {
      toast.error('Open this report from the property page to download the PDF.');
      return;
    }
    setIsDownloading(true);
    try {
      await downloadApi(REPORT_PDF_URL, 'background-check-report.pdf', {
        params: { property_id: reportPropertyId },
        headers: { Accept: 'application/pdf' },
      });
    } catch (error) {
      console.error('Error downloading Checkr report PDF:', error);
      toast.error(await getBlobErrorMessage(error, 'Failed to download the report PDF. Please try again.'));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <CheckrReportView
      report={report}
      isLoading={isLoading}
      onBack={goBack}
      onDownload={handleDownload}
      isDownloading={isDownloading}
      // report is null until the check is completed
      emptyMessage="Your background check report is not ready yet."
    />
  );
};

export default MyBackgroundCheckReport;
